import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import React from 'react'

import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffExpenses } from '../../../lib/types/models/affectedStaff/affected-staff'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import PeriodBadge from '../../elements/period-badge'

type Props = {
  affectedStaff: AffectedStaffExpenses
}

const AffectedStaffProfile: React.FC<Props> = ({ affectedStaff }) => {
  const { t } = useTranslate()
  const { navigate } = useNavigator()
  return (
    <div className="p-3 rounded-2xl bg-white-active flex items-center gap-4">
      <img
        src={affectedStaff.staff.picture?.path ?? '/profile-default.jpg'}
        alt="photo du staff"
        className="w-20 h-20 rounded-xl object-cover"
      />
      <div className="flex-1">
        <Typography variant="body-medium">
          <Typography.Link
            variant="body-medium"
            onClick={() =>
              navigate(Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', affectedStaff.id.toString()))
            }
          >
            {toFullName(affectedStaff.staff.firstName, affectedStaff.staff.lastName, {
              options: { mode: 'lastname-firstname' }
            })}
          </Typography.Link>{' '}
          -{' '}
          <span className="text-grey-lighter">
            {affectedStaff.staff.city} ({affectedStaff.staff.postalCode})
          </span>
        </Typography>
        <div className="h-2" />
        <div className="p-2 bg-grey-light rounded-sm w-full flex flex-col gap-0.5 text-grey">
          <div className="flex flex-wrap items-center">
            {[
              <Typography key="centers" variant="label-semibold">
                {affectedStaff.staffRequest.centers.map((c) => c.name).join(', ')}
              </Typography>,

              <Typography key="position" variant="label-semibold">
                {t(staffPositionEnumMapping[affectedStaff.staff.position])}
              </Typography>,

              <Typography key="formulas" variant="label-semibold">
                {affectedStaff.staffRequest.formulas.map((f) => getTranslatedName(f)).join(', ')}
              </Typography>,

              <Typography key="qualification" variant="label-semibold">
                {t(staffQualificationEnumMapping[affectedStaff.staffRequest.qualification].label)}
              </Typography>,

              <Badge
                key="transport"
                size="xsmall"
                iconPosition="start"
                icon={
                  affectedStaff.trip?.transportMeans?.picto && (
                    <img
                      height={20}
                      width={20}
                      alt="transport icon"
                      className="object-contain"
                      src={affectedStaff.trip?.transportMeans.picto.path}
                    />
                  )
                }
                className="px-2 align-middle"
                text={getTranslatedName(affectedStaff.trip?.transportMeans) || 'N/A'}
                variant="warning" // or whatever matches your design for image 2
              />,

              <Typography key="departure" variant="label-semibold">
                Lieux de départ du voyage :{' '}
                {getTranslatedName(affectedStaff.trip?.departure) || 'N/A'}
              </Typography>
            ].map((node, i) => (
              <React.Fragment key={i}>
                {i !== 0 && <span className="mx-2 text-neutral-400">|</span>}
                {node}
              </React.Fragment>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <Typography variant="label-semibold">
              Date de la RAD :{' '}
              {affectedStaff.associatedRad
                ? DateFormatter.DDMMYYYY(affectedStaff.associatedRad.date)
                : 'N/A'}{' '}
              {' | '}
            </Typography>
            <PeriodBadge
              key="period"
              period={StaffRequestProperties.getPeriod(affectedStaff.staffRequest)}
              text={`Dates de séjours : ${
                affectedStaff.staffRequest.startDate && affectedStaff.staffRequest.endDate
                  ? `${DateFormatter.DDMMYYYY(affectedStaff.staffRequest.startDate)} - ${DateFormatter.DDMMYYYY(
                      affectedStaff.staffRequest.endDate
                    )}`
                  : 'N/A'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AffectedStaffProfile
